import { Badge, Group, Text, Tooltip, useMantineTheme } from "@mantine/core";
import { IconBrandOpenai, IconBrain, IconRobot, IconServer } from "@tabler/icons-react";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useState } from "react";
import { db } from "../db";
import { providerRegistry } from "../providers";

export function ProviderBadge() {
  const theme = useMantineTheme();
  const [providerName, setProviderName] = useState<string>("");
  const [modelName, setModelName] = useState<string>("");
  const [providerIcon, setProviderIcon] = useState<JSX.Element | null>(null);

  // Get current settings from database
  const settings = useLiveQuery(async () => {
    return db.settings.where({ id: "general" }).first();
  });

  useEffect(() => {
    if (settings) {
      // Get provider info
      const providerId = settings.provider;
      const provider = providerId ? providerRegistry.get(providerId) : null;
      
      if (provider) {
        setProviderName(provider.name);
        
        // Set provider icon based on provider ID
        switch (providerId) {
          case "openai":
            setProviderIcon(<IconBrandOpenai size={16} />);
            break;
          case "anthropic":
            setProviderIcon(<IconBrain size={16} />);
            break;
          case "google":
            setProviderIcon(<IconRobot size={16} />);
            break;
          case "custom":
            setProviderIcon(<IconServer size={16} />);
            break;
          default:
            setProviderIcon(null);
        }
        
        // Get model info
        const modelId = settings.model;
        if (modelId) {
          const models = providerId ? providerRegistry.getProviderModelsAsOptions(providerId) : [];
          const model = models.find(m => m.value === modelId);
          if (model) {
            setModelName(model.label);
          } else {
            setModelName(modelId);
          }
        } else {
          setModelName("Unknown Model");
        }
      } else {
        setProviderName("Unknown Provider");
        setModelName("Unknown Model");
        setProviderIcon(null);
      }
    }
  }, [settings]);

  if (!providerName || !modelName) {
    return null;
  }

  return (
    <Tooltip label={`Using ${providerName} with ${modelName}`} position="bottom">
      <Group spacing="xs">
        <Badge 
          leftSection={providerIcon}
          color={theme.primaryColor} // This uses Mantine's theme system
          variant="filled"
          size="md"
          radius="sm"
          sx={{
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: theme.shadows.sm,
            },
          }}
        >
          {providerName}: {modelName}
        </Badge>
      </Group>
    </Tooltip>
  );
}
